/*
  Longevity-guide download gate -> HubSpot and OneSignal.

  The form markup lives in the body of the "unlocking-the-key-to-longevity-guide"
  page, and that page body ships its own inline submit handler whose
  HUBSPOT_FORM_ID is an empty string. That handler therefore posts every
  download to Shopify's /contact endpoint, and since the HubSpot Ecommerce
  Bridge stopped syncing in March 2025 nothing from /contact reaches the portal
  -- the lead is simply lost. This file takes the form over and sends the lead
  to HubSpot instead.

  Once the inline <script> is deleted from the page body in Shopify admin, the
  node-swap at the top of init() is no longer needed and can be removed.

  The download also goes to OneSignal (CAROL app), where the tag
  lead_source=longevity_guide puts the contact in the "Longevity entrants" segment
  that starts the Longevity Guide Journey: the guide now, the Zone 5 email on day 1,
  the offer on day 2. It replaces Klaviyo's flow RmVtXv (Klaviyo plan ends
  2026-10-14). These leads carry no signup_source tag, so they stay out of the
  Welcome Journey. The phone is kept as a tag only and is never texted.
*/
(function () {
  var GUIDE_URL = 'https://cdn.shopify.com/s/files/1/0648/0104/6786/files/carol-zone-2-vs-zone-5-training-guide.pdf?v=1791390403';

  var ONESIGNAL_APP_ID = 'e75eb1b9-930b-4381-bb22-246a86d3a5e2';
  var ONESIGNAL_SDK = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';

  // The portal script is already loaded site-wide by layout/theme.liquid.
  var HUBSPOT_PORTAL_ID = '7027845';

  // Paste the guide form's GUID from HubSpot (Marketing > Forms) to register
  // these downloads as real form submissions, which is what gives them form
  // analytics and campaign attribution. Leaving it empty is safe: identify()
  // below still creates or updates the contact.
  var HUBSPOT_FORM_ID = '';

  function cookie(name) {
    var hit = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return hit ? hit.pop() : '';
  }

  // Ties the download to a contact through the portal script. HubSpot keys on
  // email, so a known address updates that contact and an unknown one creates
  // it. identify() only takes effect on the next tracked event, so the
  // trackPageView is what commits the record -- without it nothing arrives.
  // A download is a lead, and the Contactable Leads segments hold no contact with an
  // empty lifecycle stage, so the stage is set alongside the email. HubSpot does not
  // move a lifecycle stage backwards by default, so an existing customer who grabs the
  // guide is not demoted to lead by this.
  //
  // The segments are also gated on the contact's Country property:
  // every one of the 2,587 members of Contactable Leads (UK) has it set, and a
  // contact without it lands in no regional list at all. Shopify already knows the
  // visitor's market, so it is mapped to the full country name HubSpot stores
  // ("US" -> "United States", "GB" -> "United Kingdom") rather than left blank.
  function marketCountry() {
    var iso = (window.Shopify && window.Shopify.country) || '';
    if (!iso) return '';
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso) || '';
    } catch (error) {
      var fallback = {
        US: 'United States', GB: 'United Kingdom', CA: 'Canada',
        AU: 'Australia', IE: 'Ireland', DE: 'Germany'
      };
      return fallback[iso] || '';
    }
  }

  function identify(email, phone) {
    var hsq = (window._hsq = window._hsq || []);
    var props = { email: email, lifecyclestage: 'lead' };
    if (phone) props.phone = phone;
    var country = marketCountry();
    if (country) props.country = country;
    hsq.push(['identify', props]);
    hsq.push(['trackPageView']);
  }

  function toHubspot(email, phone) {
    var body = {
      fields: (function () {
        var f = [
          { objectTypeId: '0-1', name: 'email', value: email },
          { objectTypeId: '0-1', name: 'phone', value: phone }
        ];
        var country = marketCountry();
        if (country) f.push({ objectTypeId: '0-1', name: 'country', value: country });
        return f;
      })(),
      context: {
        pageUri: window.location.href,
        pageName: document.title
      }
      // If the HubSpot form collects GDPR consent, submissions are rejected
      // until a legalConsentOptions block is added here. It needs a real
      // subscriptionTypeId from Settings > Marketing > Email > Subscription
      // types, so it is left out rather than guessed:
      //
      // legalConsentOptions: {
      //   legitimateInterest: {
      //     value: true,
      //     subscriptionTypeId: <id>,
      //     legalBasis: 'LEGITIMATE_INTEREST_OTHER',
      //     text: 'Requested the longevity guide download from carolbike.com.'
      //   }
      // }
    };
    var hutk = cookie('hubspotutk');
    if (hutk) body.context.hutk = hutk;
    return fetch(
      'https://api.hsforms.com/submissions/v3/integration/submit/' +
        HUBSPOT_PORTAL_ID + '/' + HUBSPOT_FORM_ID,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }
    ).then(function (res) {
      // A rejected submission still resolves, so the status has to be checked
      // or a bad field name fails silently the way the empty GUID did.
      if (!res.ok) throw new Error('HubSpot form submit failed: ' + res.status);
      return res;
    });
  }

  function toShopify(email, phone) {
    var data = new FormData();
    data.append('form_type', 'contact');
    data.append('utf8', '✓');
    data.append('contact[email]', email);
    data.append('contact[phone]', phone);
    data.append('contact[tags]', 'longevity-guide');
    data.append('contact[body]', 'Requested the longevity guide download.');
    return fetch('/contact', { method: 'POST', body: data });
  }

  // The emails pick their market lines with `region`, the same values the welcome
  // popup sets (sections/carol-welcome-popup.liquid).
  function region() {
    var iso = (window.Shopify && window.Shopify.country) || '';
    return { US: 'US', GB: 'UK', CA: 'CAN' }[iso] || 'INTL';
  }

  // "$100", "£100", "CA$100", "€100": the offer email prints this as the amount off.
  function codeValue() {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency,
        maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(100);
    } catch (error) { return '100'; }
  }

  // E.164 for every destination (HubSpot, Shopify, OneSignal). A typed + or 00 is kept;
  // otherwise the visitor's Shopify market supplies the dial code, dropping a trunk 0, the
  // same rule as the welcome popup. Bare 10-digit North American numbers still get +1.
  var DIAL = { US: '1', CA: '1', GB: '44', IE: '353', AU: '61', NZ: '64', DE: '49', FR: '33',
    NL: '31', BE: '32', CH: '41', AT: '43', ES: '34', IT: '39', SE: '46', DK: '45', NO: '47' };
  function e164(raw) {
    var s = String(raw || '').trim();
    var d = s.replace(/[^\d+]/g, '');
    if (d.charAt(0) === '+') d = '+' + d.slice(1).replace(/\+/g, '');
    else if (/^00\d/.test(d)) d = '+' + d.slice(2);
    else {
      var iso = (window.Shopify && window.Shopify.country) || '';
      var dial = DIAL[iso];
      if (/^1[2-9]\d{9}$/.test(d)) d = '+' + d;                         // US/CA with country code
      else if ((!dial || dial === '1') && /^[2-9]\d{9}$/.test(d)) d = '+1' + d; // bare US/CA
      else if (dial && dial !== '1') d = '+' + dial + d.replace(/^0+/, '');   // national number
      else if (/^0\d{10}$/.test(d)) d = '+44' + d.slice(1);                 // UK national format
    }
    return /^\+[1-9]\d{7,14}$/.test(d) ? d : '';
  }

  // Push is never asked for. The SDK starts loading when the visitor first touches the
  // form (see init), not on submit. Loading it on submit appears to have lost most mobile
  // leads, because the guide tab took over before login/addEmail had run: Meta counted
  // 11 Lead events on 13-15 Oct 2026, 9 of them on phones, while the OneSignal segment
  // held 4 entrants in total since 7 Oct.
  function loadOneSignal() {
    return new Promise(function (resolve, reject) {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(function (OneSignal) { resolve(OneSignal); });
      if (document.querySelector('script[src="' + ONESIGNAL_SDK + '"]')) return;
      var s = document.createElement('script');
      s.src = ONESIGNAL_SDK; s.defer = true;
      s.onerror = function () { reject(new Error('sdk')); };
      document.head.appendChild(s);
      setTimeout(function () { reject(new Error('timeout')); }, 15000);
    });
  }

  // The lowercased email is the external_id, as in the welcome popup and the cart
  // Flow, so an `order_placed` event ends this Journey too.
  function toOneSignal(email, phone) {
    var addr = email.toLowerCase();
    var tags = { lead_source: 'longevity_guide', region: region(), code_value: codeValue() };
    var number = e164(phone);
    if (number) tags.phone = number;
    return readyOneSignal().then(function (OneSignal) {
      return OneSignal.login(addr).then(function () {
        OneSignal.User.addEmail(addr);
        OneSignal.User.addTags(tags);
      });
    });
  }

  // Loads and initialises the SDK once. A failure is forgotten, so submit can retry.
  var oneSignalReady = null;
  function readyOneSignal() {
    if (!oneSignalReady) {
      oneSignalReady = loadOneSignal().then(function (OneSignal) {
        return OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          autoResubscribe: false,
          notifyButton: { enable: false },
          welcomeNotification: { disable: true },
          promptOptions: { slidedown: { prompts: [] } }
        }).then(function () { return OneSignal; });
      });
      oneSignalReady.catch(function () { oneSignalReady = null; });
    }
    return oneSignalReady;
  }

  // Phones and tablets, including the Facebook and Instagram in-app browsers that the
  // guide ads open in. There a new tab replaces or suspends this page at once.
  function touchDevice() {
    return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  }

  function within(ms, promise) {
    return Promise.race([promise, new Promise(function (resolve) { setTimeout(resolve, ms); })]);
  }

  function init() {
    var original = document.getElementById('cs-guide-form');
    if (!original) return;

    // Swapping in a clone drops the page body's own submit listener, so this
    // file is the only handler and the lead is not posted twice.
    var form = original.cloneNode(true);
    original.parentNode.replaceChild(form, original);

    // Re-query: the status node is inside the form and was cloned with it.
    var status = form.querySelector('#cs-guide-status');

    // Start the OneSignal SDK while the visitor is still typing.
    form.addEventListener('focusin', function () {
      readyOneSignal().catch(function () {});
    }, { once: true });

    // Show the international format as soon as the visitor leaves the field.
    form.phone.addEventListener('change', function () {
      var n = e164(form.phone.value);
      if (n) form.phone.value = n;
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var email = form.email.value.trim();
      var phone = form.phone.value.trim();
      // Normalise once, in the field too, so HubSpot's form capture, identify(), Shopify and
      // OneSignal all receive the same E.164 number. Unparseable input passes through unchanged.
      var normalized = e164(phone);
      if (normalized) { phone = normalized; form.phone.value = normalized; }
      if (!email || form.email.validity.typeMismatch) {
        status.textContent = 'Please enter a valid email address.';
        form.email.focus();
        return;
      }
      if (!phone) {
        status.textContent = 'Please enter a phone number.';
        form.phone.focus();
        return;
      }

      // On a phone the guide opens in this tab, once OneSignal has the lead or after
      // 4 seconds at most. A same-tab navigation needs no user gesture, so the wait
      // is safe there.
      var mobile = touchDevice();

      // On desktop, open the guide inside the click that submitted the form.
      // Waiting for the network call first would spend the user gesture and the
      // popup blocker would swallow the tab.
      var tab = mobile ? null : window.open(GUIDE_URL, '_blank', 'noopener');

      status.textContent = mobile
        ? 'Thanks — your guide is opening.'
        : tab
          ? 'Thanks — your guide is opening in a new tab.'
          : 'Thanks. Your browser blocked the new tab, so open the guide here.';

      if (!tab) {
        var link = document.createElement('a');
        link.href = GUIDE_URL;
        link.target = '_blank';
        link.rel = 'noopener';
        link.className = 'cs-gate__fallback';
        link.textContent = 'Open the guide';
        status.appendChild(document.createTextNode(' '));
        status.appendChild(link);
      }

      // Meta Lead event. The guide ad set optimises for it, and before this
      // nothing on the site sent one. fbq is loaded by the Meta app embed and is
      // missing when the visitor declined tracking, so it is checked first.
      try {
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', { content_name: 'Longevity guide' });
        }
      } catch (error) {}

      // The guide emails. A failure here must not block the guide or HubSpot.
      var sent;
      try { sent = toOneSignal(email, phone).catch(function () {}); } catch (error) {}
      if (mobile) {
        within(4000, sent || Promise.resolve()).then(function () {
          window.location.assign(GUIDE_URL);
        });
      }

      // identify() needs no HubSpot configuration, so it runs first and is what
      // guarantees the contact exists.
      var tracked = !!cookie('hubspotutk');
      try {
        identify(email, phone);
      } catch (error) {
        tracked = false;
      }

      function fallback() {
        /* The guide is already open; a failed lead post must not block it. */
        toShopify(email, phone).catch(function () {});
      }

      if (HUBSPOT_FORM_ID) {
        toHubspot(email, phone).catch(fallback);
      } else if (!tracked) {
        // No form GUID and the portal script never set its cookie, so nothing
        // above reached HubSpot.
        fallback();
      }

      form.reset();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
